"use client";

import { cn } from "@/utils/cn";
import { Switch as AriaSwitch, SwitchProps as AriaSwitchProps, Label, Text } from "react-aria-components";

export interface SwitchProps extends Omit<AriaSwitchProps, "children"> {
	label?: string; // prefix label
	suffixLabel?: string; // suffix label
	description?: string;
	errorMessage?: string;
}

/**
 * Horizontally-aligned switch:
 *   [prefix label]  [toggle]  [suffix label]
 * Description and error message appear beneath.
 */
export function SwitchInput({ label, suffixLabel, description, errorMessage, className, ...props }: SwitchProps) {
	return (
		<div className={cn("flex flex-col py-3 my-3 gap-2", className)}>
			{/* Row: prefix label – switch – suffix label */}
			<div className="flex items-center gap-2">
				{label && <Label className="text-title-medium">{label}</Label>}

				<AriaSwitch
					{...props}
					className={({ isSelected, isDisabled, isFocusVisible }) =>
						cn(
							"group inline-flex h-6 w-11 shrink-0 items-center rounded-full border-2 border-transparent transition-colors",
							isDisabled && "cursor-not-allowed opacity-50",
							isSelected ? "bg-primary" : "bg-input",
							isFocusVisible && "outline-none ring-2 ring-ring ring-offset-2 ring-offset-background",
						)
					}
				>
					{({ isSelected }) => (
						<>
							<span className="sr-only">{isSelected ? "On" : "Off"}</span>
							<div
								className={cn(
									"pointer-events-none block size-5 rounded-full bg-background shadow-lg ring-0 transition-transform",
									isSelected ? "translate-x-5" : "translate-x-0",
								)}
							/>
						</>
					)}
				</AriaSwitch>

				{suffixLabel && <span className="text-body text-muted-foreground">{suffixLabel}</span>}
			</div>

			{description && (
				<Text slot="description" className="text-body-small">
					{description}
				</Text>
			)}

			<Text slot="errorMessage" className="text-body-small-medium text-destructive h-1">
				{errorMessage || ""}
			</Text>
		</div>
	);
}
